import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Minus, Plus, Check, RefreshCw, ScanLine, PackageCheck,
  Box, AlertTriangle, RotateCcw, User, ClipboardCheck, Truck, Copy,
  Pencil, Trash2, Search, ChevronDown, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// --- audio feedback ---------------------------------------------------------
const beep = (freq, dur, type = 'sine') => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = freq; o.type = type; g.gain.value = 0.3;
    o.start(); setTimeout(() => o.stop(), dur);
  } catch { /* no audio */ }
};
const ok = () => { beep(820, 130); if (navigator.vibrate) navigator.vibrate(90); };
const bad = () => { beep(300, 280, 'square'); if (navigator.vibrate) navigator.vibrate([180, 90, 180]); };

const MobilePickAndPack = () => {
  const navigate = useNavigate();

  const [staffId, setStaffId] = useState(localStorage.getItem('pp_staff_id') || '');
  const [staffInput, setStaffInput] = useState(localStorage.getItem('pp_staff_id') || '');
  const [phase, setPhase] = useState('staff'); // staff|loading|picking|packing|done|blocked
  const [blockedMsg, setBlockedMsg] = useState('');
  const [slip, setSlip] = useState(null); // {pick_slip_number, customer, is_seasonal}

  // ---- picking ----
  const [pick, setPick] = useState(null); // next-item guidance
  const [qty, setQty] = useState(0);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // confirmed-pick corrections (edit / delete before packing)
  const [confirmedItems, setConfirmedItems] = useState([]);
  const [showConfirmed, setShowConfirmed] = useState(false);
  const [editItem, setEditItem] = useState(null); // the confirmed line being edited
  const [editQty, setEditQty] = useState(0);
  const [editReason, setEditReason] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  // ---- packing ----
  const [lines, setLines] = useState([]);        // packing items (per line)
  const [boxes, setBoxes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [cartonScans, setCartonScans] = useState([]); // current open carton
  const [extras, setExtras] = useState([]);
  const [errorUsns, setErrorUsns] = useState([]);     // all rejected USNs from last close
  const [errorFilter, setErrorFilter] = useState(''); // search within the rejected USNs
  const [showErrors, setShowErrors] = useState(false);
  const [pendingClose, setPendingClose] = useState(null); // {externalBoxNo, acceptedCount} when a close is gated on error acknowledgment
  const [lastBox, setLastBox] = useState(null);       // just-closed carton
  const [showSticker, setShowSticker] = useState(false);
  const [sticker, setSticker] = useState('');
  const scanRef = useRef(null);
  const sessionScannedRef = useRef(new Set()); // every USN already queued/packed this session

  // =====================================================
  // BOOT — resolve the picker's assigned slip
  // =====================================================
  const boot = useCallback(async (sid) => {
    setPhase('loading');
    try {
      // 1) SESSION RECOVERY — resume this staff's unfinished combined slip
      //    (survives an accidental refresh, in picking OR packing phase).
      try {
        const act = await apiClient.get(`/wms/pick-and-pack/active?staff_id=${encodeURIComponent(sid)}`);
        if (act.data?.has_slip && act.data.slip) {
          const r = act.data.slip;
          setSlip({ pick_slip_number: r.pick_slip_number, customer: r.customer || '', is_seasonal: !!r.is_seasonal });
          if (act.data.phase === 'packing') {
            await enterPacking(r.pick_slip_number);
          } else {
            await apiClient.post('/wms/pick-and-pack/start', { slip_number: r.pick_slip_number }).catch(() => {});
            await loadNextItem(r.pick_slip_number);
            setPhase('picking');
          }
          return;
        }
      } catch (_) { /* no active slip → assign a new one below */ }

      // 2) No active slip → assign the next one (FIFO).
      const res = await apiClient.get(`/wms/fg/pick-slips/my-assigned?staff_id=${encodeURIComponent(sid)}`);
      const data = res.data;
      if (!data.has_slip || !data.slip) {
        setBlockedMsg(data.message || 'No pick slips available in your queue right now.');
        setPhase('blocked');
        return;
      }
      const s = data.slip;
      if (s.slip_type === 'FG_TRANSFER') {
        setBlockedMsg('Your next slip is a Design-Transfer slip — please use the classic FG Picking screen for it.');
        setPhase('blocked');
        return;
      }
      // Stamp as combined (blocks old-flow in-progress slips server-side).
      let startRes;
      try {
        startRes = await apiClient.post('/wms/pick-and-pack/start', { slip_number: s.pick_slip_number });
      } catch (e) {
        setBlockedMsg(e.response?.data?.detail || 'Could not start this slip in Pick & Pack.');
        setPhase('blocked');
        return;
      }
      setSlip({
        pick_slip_number: s.pick_slip_number,
        customer: startRes.data.customer || s.customer || '',
        is_seasonal: !!startRes.data.is_seasonal,
      });
      if (startRes.data.picking_done) {
        await enterPacking(s.pick_slip_number);
      } else {
        await loadNextItem(s.pick_slip_number);
        setPhase('picking');
      }
    } catch (e) {
      setBlockedMsg(e.response?.data?.detail || 'Failed to load your assigned slip.');
      setPhase('blocked');
    }
  }, []);

  // Auto-resume on mount/refresh if a staff is already remembered on this device.
  useEffect(() => {
    const saved = localStorage.getItem('pp_staff_id');
    if (saved) { setStaffId(saved); boot(saved); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchStaff = () => {
    localStorage.removeItem('pp_staff_id');
    setStaffId(''); setStaffInput('');
    setSlip(null); setPick(null); setLines([]); setBoxes([]);
    setCartonScans([]); setExtras([]); setLastBox(null); setGroup(null);
    setConfirmedItems([]); setShowConfirmed(false); setEditItem(null);
    sessionScannedRef.current = new Set();
    setPhase('staff');
  };

  const loadNextItem = async (slipNo) => {
    const res = await apiClient.get(`/wms/pick-and-pack/next-item?slip_number=${encodeURIComponent(slipNo)}`);
    const g = res.data;
    loadConfirmedItems(slipNo);
    if (!g.has_next) { await enterPacking(slipNo); return; }
    setPick(g);
    setQty(g.item?.remaining_qty ?? g.item?.required_qty ?? 0);
    setReason('');
  };

  // Already-confirmed pick lines (for the Edit/Delete correction list).
  const loadConfirmedItems = async (slipNo) => {
    try {
      const res = await apiClient.get(`/wms/pick-and-pack/confirmed-items?slip_number=${encodeURIComponent(slipNo)}`);
      setConfirmedItems(res.data.items || []);
    } catch { /* non-fatal */ }
  };

  const openEdit = (ci) => {
    setEditItem(ci);
    setEditQty(ci.confirmed_qty);
    setEditReason(ci.confirm_reason || '');
  };

  const saveEdit = async () => {
    if (!editItem || editBusy) return;
    const confirmed = Math.max(0, Math.min(parseInt(editQty) || 0, editItem.required_qty));
    const isShort = confirmed < editItem.required_qty;
    if (isShort && !editReason.trim()) {
      toast.error('Please enter a reason for the short / skipped quantity.');
      return;
    }
    setEditBusy(true);
    try {
      await apiClient.post('/wms/pick-and-pack/confirm-item', {
        slip_number: slip.pick_slip_number,
        item_index: editItem.index,
        confirmed_qty: confirmed,
        reason: editReason.trim() || undefined,
      });
      ok();
      toast.success(`Updated to ${confirmed} pc`);
      setEditItem(null);
      await loadConfirmedItems(slip.pick_slip_number);
    } catch (e) {
      bad();
      toast.error(e.response?.data?.detail || 'Failed to update quantity');
    } finally { setEditBusy(false); }
  };

  const deleteConfirmed = async (ci) => {
    if (editBusy) return;
    setEditBusy(true);
    try {
      await apiClient.post('/wms/pick-and-pack/unconfirm-item', {
        slip_number: slip.pick_slip_number,
        item_index: ci.index,
      });
      ok();
      toast.success(`${ci.design_name || ci.barcode} reset — pick it again`);
      await loadConfirmedItems(slip.pick_slip_number);
    } catch (e) {
      bad();
      toast.error(e.response?.data?.detail || 'Could not reset this line');
    } finally { setEditBusy(false); }
  };

  const handleStaff = (e) => {
    e?.preventDefault?.();
    const sid = staffInput.trim();
    if (!sid) return;
    localStorage.setItem('pp_staff_id', sid);
    setStaffId(sid);
    boot(sid);
  };

  // =====================================================
  // PICKING — scan-free quantity confirmation
  // =====================================================
  const confirmItem = async () => {
    if (!pick?.item || busy) return;
    const item = pick.item;
    const confirmed = Math.max(0, Math.min(parseInt(qty) || 0, item.required_qty));
    const isShort = confirmed < item.required_qty;
    if (isShort && !reason.trim()) {
      toast.error('Please enter a reason for the short / skipped quantity.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.post('/wms/pick-and-pack/confirm-item', {
        slip_number: slip.pick_slip_number,
        item_index: item.index,
        confirmed_qty: confirmed,
        reason: reason.trim() || undefined,
      });
      ok();
      if (res.data.all_confirmed) {
        toast.success('All items confirmed — opening packing', { duration: 1500, position: 'top-center' });
        await enterPacking(slip.pick_slip_number);
      } else {
        const g = res.data.next;
        setPick(g);
        setQty(g.item?.remaining_qty ?? g.item?.required_qty ?? 0);
        setReason('');
        loadConfirmedItems(slip.pick_slip_number);
      }
    } catch (e) {
      bad();
      toast.error(e.response?.data?.detail || 'Failed to confirm item');
    } finally {
      setBusy(false);
    }
  };

  const forceSubmit = async () => {
    const r = window.prompt('Force Submit — keep what is confirmed, skip the rest.\nEnter a reason (required):');
    if (r === null) return;
    if (!r.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await apiClient.post('/wms/fg/pick-slips/force-submit', { slip_number: slip.pick_slip_number, remarks: r.trim() });
      ok();
      toast.success('Slip force-submitted — opening packing');
      await enterPacking(slip.pick_slip_number);
    } catch (e) {
      bad();
      toast.error(e.response?.data?.detail || 'Force submit failed');
    } finally { setBusy(false); }
  };

  // =====================================================
  // PACKING — the single scan + cartons
  // =====================================================
  const fetchPacking = async (slipNo) => {
    const [itemsRes, boxesRes] = await Promise.all([
      apiClient.get(`/wms/packing/slip/${slipNo}/items`),
      apiClient.get(`/wms/packing/boxes/${slipNo}`),
    ]);
    setLines(itemsRes.data.items || []);
    setBoxes(boxesRes.data.boxes || []);
    return itemsRes.data;
  };

  const enterPacking = async (slipNo) => {
    setPhase('loading');
    try {
      const [itemsData] = await Promise.all([
        fetchPacking(slipNo),
        apiClient.get('/wms/dispatch/packer-groups').then(r => setGroups(r.data?.groups || [])).catch(() => setGroups([])),
      ]);
      const isSeasonal = !!itemsData.is_seasonal;
      setSlip((prev) => ({ ...(prev || {}), pick_slip_number: slipNo, customer: itemsData.customer || prev?.customer || '', is_seasonal: isSeasonal }));
      setCartonScans([]);
      if ((itemsData.items || []).length === 0) { setPhase('done'); return; }
      setPhase('packing');
      setTimeout(() => scanRef.current?.focus(), 150);
    } catch (e) {
      toast.error('Failed to open packing screen');
      setBlockedMsg('Failed to open packing screen.');
      setPhase('blocked');
    }
  };

  // INSTANT local scan — no server round-trip (matches old FG Picking speed).
  // The whole carton is validated + flipped + boxed in ONE call at Close Carton.
  const handleScan = (e) => {
    e.preventDefault();
    const usn = scanInput.trim().toUpperCase();
    setScanInput('');
    if (!usn) { scanRef.current?.focus(); return; }
    // USN codes are always 9 characters. A shorter/longer scan is almost always
    // the SKU code scanned by mistake — reject it at the point of scan so the
    // operator catches it while still holding the piece.
    if (usn.length !== 9) { addPack(usn); return; }
    if (sessionScannedRef.current.has(usn) || cartonScans.some((s) => s.usn === usn)) {
      bad();
      toast.error(`${usn} already scanned`, { duration: 1500 });
      scanRef.current?.focus();
      return;
    }
    ok();
    setCartonScans((prev) => [...prev, { usn }]);
    scanRef.current?.focus();
  };

  // Not a 9-char USN: maybe an FG PACK code (e.g. a common-pool small box) → add its pieces.
  const addPack = async (code) => {
    if (cartonScans.some((s) => s.pack === code)) { bad(); toast.error(`Pack ${code} already in this carton`, { duration: 1500 }); scanRef.current?.focus(); return; }
    try {
      const r = (await apiClient.get('/wms/pick-and-pack/pack/resolve-code', { params: { slip_number: slip?.pick_slip_number, code } })).data;
      const fresh = r.members.filter((m) => !sessionScannedRef.current.has(m) && !cartonScans.some((s) => s.usn === m));
      const inCarton = cartonScans.filter((s) => s.sku === r.sku_code).length;
      const room = Math.max(0, (r.needed || 0) - inCarton);
      if (fresh.length === 0) { bad(); toast.error(`Pack ${code} has no pieces left to pack`, { duration: 2500 }); }
      else if (fresh.length > room) {
        bad(); toast.error(`Pack ${code} holds ${fresh.length} pcs but only ${room} still needed — open the pack and scan single pieces`, { duration: 4000 });
      } else {
        ok();
        setCartonScans((prev) => [...prev, ...fresh.map((m) => ({ usn: m, pack: code, sku: r.sku_code }))]);
        toast.success(`Pack ${code} · ${fresh.length} pc(s) added${r.real_usns ? '' : ' (pack piece codes)'}`, { duration: 1800 });
      }
    } catch (e) {
      bad();
      toast.error(e.response?.data?.detail || `"${code}" is not a USN — scan the 9-character USN, not the SKU code`, { duration: 2500 });
    }
    scanRef.current?.focus();
  };

  const removeScan = (usn) => setCartonScans((prev) => {
    const hit = prev.find((s) => s.usn === usn);
    return hit?.pack ? prev.filter((s) => s.pack !== hit.pack) : prev.filter((s) => s.usn !== usn);
  });

  const closeCarton = () => {
    if (cartonScans.length === 0) { toast.error('Scan at least one piece into the carton'); return; }
    if (!group) { toast.error('Select a packer group first'); return; }
    if (slip?.is_seasonal) { setSticker(''); setShowSticker(true); return; }
    doCloseCarton(null);
  };

  // ONE batch write: resolve + validate + flip stock + create the box.
  // Two-phase: pass confirm=false first. If the server reports rejected USNs it
  // commits NOTHING and asks for acknowledgment — we show the error dialog and
  // only commit (confirm=true) after the operator pulls the bad pieces out.
  const doCloseCarton = async (externalBoxNo, confirm = false) => {
    if (cartonScans.length === 0 || !group) return;
    const usns = cartonScans.map((s) => s.usn);
    setBusy(true);
    try {
      const res = await apiClient.post('/wms/pick-and-pack/pack/close-carton', {
        slip_number: slip.pick_slip_number,
        usn_codes: usns,
        group_id: group.group_id,
        external_box_no: externalBoxNo || undefined,
        confirm,
      });

      // GATE: errors found, nothing committed yet — surface them for acknowledgment.
      if (res.data.requires_acknowledgment) {
        bad();
        setErrorUsns(res.data.extras || []);
        setErrorFilter('');
        setPendingClose({ externalBoxNo: externalBoxNo || null, acceptedCount: res.data.accepted_count || 0 });
        setShowSticker(false);
        setShowErrors(true);
        return;
      }

      ok();
      setPendingClose(null);
      const box = res.data.box;
      const respExtras = res.data.extras || [];
      const extraUsns = new Set(respExtras.map((x) => x.usn_code));
      // everything accepted is now locked for the session
      usns.forEach((u) => { if (!extraUsns.has(u)) sessionScannedRef.current.add(u); });

      if (box) {
        setLastBox({ box_number: box.box_number, external_box_no: box.external_box_no, total_qty: box.total_qty });
      }
      const putaway = respExtras.filter((x) => x.reason !== 'duplicate');
      const dupCount = respExtras.length - putaway.length;
      if (putaway.length) {
        setExtras((prev) => [...prev, ...putaway
          .filter((p) => !prev.some((x) => x.usn_code === p.usn_code))
          .map((p) => ({ usn_code: p.usn_code, original_bin: p.original_bin, sku_code: p.sku_code, design_name: p.design_name }))]);
        toast.error(`${putaway.length} extra/over piece(s) kept aside for put-away`, { duration: 4000 });
      }
      if (dupCount) toast.error(`${dupCount} duplicate scan(s) ignored`);

      setShowErrors(false);
      setShowSticker(false); setSticker('');
      setCartonScans([]);
      const data = await fetchPacking(slip.pick_slip_number);
      if (box) toast.success(`Carton ${box.box_number} closed · ${box.total_qty} pcs`);
      else toast.message('No pieces packed — all were kept aside');
      if ((data.items || []).length === 0) setPhase('done');
    } catch (e) {
      bad();
      toast.error(e.response?.data?.detail || 'Failed to close carton');
    } finally { setBusy(false); }
  };

  // Operator acknowledged the rejected USNs — commit the box with the valid pieces.
  const proceedCloseAfterAck = () => {
    const ext = pendingClose?.externalBoxNo || null;
    setShowErrors(false);
    doCloseCarton(ext, true);
  };

  const REASON_META = {
    duplicate:     { label: 'Already scanned', hint: 'Already packed/scanned — pull it out of this carton', text: 'text-amber-400', bg: 'bg-amber-500/10 border border-amber-600/40' },
    not_in_stock:  { label: 'Not in stock', hint: 'Not STORED / already moved — remove & verify the piece', text: 'text-red-400', bg: 'bg-red-500/10 border border-red-600/40' },
    extra_or_over: { label: 'Extra / over qty', hint: 'Beyond the required quantity — keep aside for put-away', text: 'text-orange-400', bg: 'bg-orange-500/10 border border-orange-600/40' },
  };

  // 1-based position of a USN in the scan order of the current carton (0 = not found).
  const scanPosOf = (usn) => cartonScans.findIndex((s) => s.usn === usn) + 1;

  const copyErrorUsns = () => {
    const text = errorUsns.map((e) => {
      const pos = scanPosOf(e.usn_code);
      return `${pos ? `#${pos}` : ''}\t${e.usn_code}\t${REASON_META[e.reason]?.label || e.reason}\t${e.sku_code || ''}`;
    }).join('\n');
    try { navigator.clipboard?.writeText(text); toast.success('USN list copied'); } catch (_) { toast.error('Copy failed'); }
  };

  const confirmPutaway = async () => {
    if (extras.length === 0) return;
    try {
      const res = await apiClient.post('/wms/packing/extra-pieces/confirm-putaway', { usn_codes: extras.map((p) => p.usn_code) });
      toast.success(res.data.message || 'Extra pieces marked for put-away');
      setExtras([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm put-away');
    }
  };

  useEffect(() => { if (scanRef.current && phase === 'packing') scanRef.current.focus(); }, [phase, cartonScans.length]);

  // =====================================================
  // RENDER
  // =====================================================
  const Header = ({ right }) => (
    <header className="bg-[#140b22] text-white sticky top-0 z-20 border-b border-violet-900/50">
      <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate('/wms/mobile')} data-testid="pp-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-fuchsia-400" />
              <h1 className="text-lg font-bold tracking-tight">Pick &amp; Pack</h1>
            </div>
            {slip?.pick_slip_number && <p className="text-xs text-violet-300 font-mono">{slip.pick_slip_number}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {right}
          {staffId && phase !== 'staff' && (
            <Button variant="ghost" size="sm" onClick={switchStaff}
              className="text-violet-200 hover:bg-white/10 h-8 px-2" data-testid="pp-switch-staff-btn">
              <User className="w-4 h-4 mr-1" /> {staffId}
            </Button>
          )}
        </div>
      </div>
    </header>
  );

  // ---- STAFF GATE ----
  if (phase === 'staff') {
    return (
      <div className="min-h-screen bg-[#0b0614] text-white">
        <Header />
        <main className="max-w-md mx-auto px-5 py-16">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-fuchsia-500/20 to-violet-700/20 border border-violet-700/40 mb-4">
              <User className="w-8 h-8 text-fuchsia-300" />
            </div>
            <h2 className="text-2xl font-bold">Scan your badge</h2>
            <p className="text-violet-300/80 text-sm mt-1">A pick slip will be assigned to you (FIFO).</p>
          </div>
          <form onSubmit={handleStaff} className="space-y-4">
            <Input
              autoFocus value={staffInput} onChange={(e) => setStaffInput(e.target.value)}
              placeholder="Staff ID (e.g. V0104)"
              className="text-center text-lg font-mono bg-[#170e28] border-violet-800 text-white h-14"
              data-testid="pp-staff-input"
            />
            <Button type="submit" disabled={!staffInput.trim()} className="w-full h-14 text-base bg-gradient-to-r from-fuchsia-600 to-violet-700 hover:opacity-90" data-testid="pp-staff-submit">
              Start
            </Button>
          </form>
        </main>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#0b0614] text-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <RefreshCw className="w-8 h-8 animate-spin text-fuchsia-400" />
        </div>
      </div>
    );
  }

  if (phase === 'blocked') {
    return (
      <div className="min-h-screen bg-[#0b0614] text-white">
        <Header />
        <main className="max-w-md mx-auto px-5 py-20 text-center">
          <div className="inline-flex p-4 rounded-2xl bg-amber-500/15 border border-amber-600/40 mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>
          <p className="text-lg text-amber-100" data-testid="pp-blocked-msg">{blockedMsg}</p>
          <div className="mt-8 flex flex-col gap-3">
            <Button onClick={() => boot(staffId)} className="bg-violet-700 hover:bg-violet-800" data-testid="pp-retry-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Check again
            </Button>
            <Button variant="outline" onClick={() => { setPhase('staff'); }} className="border-violet-700 text-violet-200 hover:bg-white/5">
              Change staff
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // ---- PICKING PHASE ----
  if (phase === 'picking' && pick?.item) {
    const item = pick.item;
    const bins = pick.bins || [];
    const fifo = bins[0];
    const prog = pick.progress || { completed_items: 0, total_items: 0 };
    const isShort = (parseInt(qty) || 0) < item.required_qty;
    return (
      <div className="min-h-screen bg-[#0b0614] text-white pb-40">
        <Header right={
          <Badge className="bg-violet-800 text-violet-100" data-testid="pp-pick-progress">
            {prog.completed_items}/{prog.total_items} items
          </Badge>
        } />
        <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fuchsia-400/80 font-semibold">
            <ClipboardCheck className="w-4 h-4" /> Step 1 · Confirm picked quantity
          </div>

          {/* FIFO bin card */}
          <div className="rounded-2xl bg-gradient-to-br from-emerald-600/20 to-emerald-900/10 border border-emerald-700/40 p-5" data-testid="pp-fifo-bin-card">
            <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider font-semibold mb-1">
              <MapPin className="w-4 h-4" /> Pick from bin
            </div>
            <div className="text-3xl font-black font-mono text-emerald-200" data-testid="pp-fifo-bin">{fifo?.bin_code || '—'}</div>
            <div className="text-xs text-emerald-400/70 mt-1">
              {fifo ? `${fifo.available_qty} pcs available here` : 'No FIFO bin found'}
              {bins.length > 1 && <span className="ml-2 text-emerald-300/60">· next: {bins.slice(1, 3).map((b) => b.bin_code).join(', ')}</span>}
            </div>
          </div>

          {/* item */}
          <div className="rounded-2xl bg-[#160d26] border border-violet-900/60 p-5">
            <div className="text-xl font-bold">{item.design_name || item.barcode}</div>
            <div className="text-sm text-violet-300 mt-1">
              {[item.shade, item.size].filter(Boolean).join(' · ')}
            </div>
            <div className="text-xs text-violet-400/70 font-mono mt-1">{item.barcode}</div>
            {item.remarks && <div className="text-xs text-amber-300 mt-2">Note: {item.remarks}</div>}

            <div className="mt-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-violet-400/70 uppercase tracking-wider">Required</div>
                <div className="text-4xl font-black" data-testid="pp-required-qty">{item.required_qty}</div>
              </div>
              {/* stepper */}
              <div className="flex items-center gap-3">
                <Button size="icon" variant="outline" className="h-12 w-12 rounded-full border-violet-700 text-white bg-[#1f1235] hover:bg-[#291746]"
                  onClick={() => setQty((q) => Math.max(0, (parseInt(q) || 0) - 1))} data-testid="pp-qty-minus">
                  <Minus className="w-5 h-5" />
                </Button>
                <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
                  className="w-20 h-14 text-center text-2xl font-black bg-[#0b0614] border-violet-700 text-white"
                  data-testid="pp-qty-input" />
                <Button size="icon" variant="outline" className="h-12 w-12 rounded-full border-violet-700 text-white bg-[#1f1235] hover:bg-[#291746]"
                  onClick={() => setQty((q) => Math.min(item.required_qty, (parseInt(q) || 0) + 1))} data-testid="pp-qty-plus">
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {isShort && (
              <div className="mt-4">
                <label className="text-xs text-amber-300 font-medium">Reason for short / skip (required)</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. stock short in bin"
                  className="mt-1 bg-[#0b0614] border-amber-700/60 text-white" data-testid="pp-reason-input" />
              </div>
            )}
          </div>

          {/* confirmed-pick corrections — fix a wrong qty before packing */}
          {confirmedItems.length > 0 && (
            <div className="rounded-2xl bg-[#140b22] border border-violet-900/60 overflow-hidden" data-testid="pp-confirmed-card">
              <button type="button" onClick={() => setShowConfirmed((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5"
                data-testid="pp-confirmed-toggle">
                <span className="flex items-center gap-2 text-sm font-semibold text-violet-100">
                  <ListChecks className="w-4 h-4 text-emerald-400" />
                  Confirmed items ({confirmedItems.length})
                </span>
                <ChevronDown className={`w-4 h-4 text-violet-400 transition-transform ${showConfirmed ? 'rotate-180' : ''}`} />
              </button>
              {showConfirmed && (
                <div className="border-t border-violet-900/60 divide-y divide-violet-950/70" data-testid="pp-confirmed-list">
                  {confirmedItems.map((ci) => (
                    <div key={ci.index} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`pp-confirmed-row-${ci.index}`}>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{ci.design_name || ci.barcode}</div>
                        <div className="text-xs text-violet-400">{[ci.shade, ci.size].filter(Boolean).join(' · ')}</div>
                        {ci.is_partial && ci.confirm_reason && (
                          <div className="text-[11px] text-amber-300/80 mt-0.5 truncate">Reason: {ci.confirm_reason}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className={`font-black ${ci.is_partial ? 'text-amber-300' : 'text-emerald-300'}`} data-testid={`pp-confirmed-qty-${ci.index}`}>
                            {ci.confirmed_qty}/{ci.required_qty}
                          </div>
                        </div>
                        <Button size="icon" variant="outline" className="h-9 w-9 rounded-full border-violet-700 text-violet-200 bg-[#1f1235] hover:bg-[#291746]"
                          onClick={() => openEdit(ci)} disabled={editBusy} data-testid={`pp-edit-confirmed-${ci.index}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-9 w-9 rounded-full border-red-800 text-red-300 bg-red-950/30 hover:bg-red-900/40"
                          onClick={() => deleteConfirmed(ci)} disabled={editBusy} data-testid={`pp-delete-confirmed-${ci.index}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>

        {/* edit a confirmed quantity */}
        <Dialog open={!!editItem} onOpenChange={(o) => { if (!o && !editBusy) setEditItem(null); }}>
          <DialogContent className="sm:max-w-sm bg-[#140b22] border-violet-800 text-white" data-testid="pp-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-fuchsia-400" /> Correct picked quantity</DialogTitle>
              <DialogDescription className="text-violet-300">
                {editItem?.design_name || editItem?.barcode} — required {editItem?.required_qty}.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center gap-4 py-2">
              <Button size="icon" variant="outline" className="h-12 w-12 rounded-full border-violet-700 text-white bg-[#1f1235] hover:bg-[#291746]"
                onClick={() => setEditQty((q) => Math.max(0, (parseInt(q) || 0) - 1))} data-testid="pp-edit-qty-minus">
                <Minus className="w-5 h-5" />
              </Button>
              <Input type="number" value={editQty} onChange={(e) => setEditQty(e.target.value)}
                className="w-24 h-14 text-center text-2xl font-black bg-[#0b0614] border-violet-700 text-white" data-testid="pp-edit-qty-input" />
              <Button size="icon" variant="outline" className="h-12 w-12 rounded-full border-violet-700 text-white bg-[#1f1235] hover:bg-[#291746]"
                onClick={() => setEditQty((q) => Math.min(editItem?.required_qty ?? 0, (parseInt(q) || 0) + 1))} data-testid="pp-edit-qty-plus">
                <Plus className="w-5 h-5" />
              </Button>
            </div>
            {editItem && (parseInt(editQty) || 0) < editItem.required_qty && (
              <div>
                <label className="text-xs text-amber-300 font-medium">Reason for short / skip (required)</label>
                <Input value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="e.g. miscounted"
                  className="mt-1 bg-[#0b0614] border-amber-700/60 text-white" data-testid="pp-edit-reason-input" />
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" className="border-violet-700 text-violet-200 hover:bg-violet-800/30" onClick={() => setEditItem(null)} disabled={editBusy} data-testid="pp-edit-cancel">Cancel</Button>
              <Button className="bg-gradient-to-r from-emerald-600 to-emerald-700" onClick={saveEdit} disabled={editBusy} data-testid="pp-edit-save">
                {editBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* sticky action bar */}
        <div className="fixed bottom-0 inset-x-0 bg-[#140b22]/95 backdrop-blur border-t border-violet-900/60 p-4">
          <div className="max-w-2xl mx-auto flex gap-3">
            {slip?.is_seasonal && (
              <Button variant="outline" className="border-amber-600 text-amber-300 hover:bg-amber-500/10" onClick={forceSubmit} disabled={busy} data-testid="pp-force-submit-btn">
                Force Submit
              </Button>
            )}
            <Button className="flex-1 h-14 text-base bg-gradient-to-r from-emerald-600 to-emerald-700 hover:opacity-90 disabled:opacity-40" onClick={confirmItem} disabled={busy || (isShort && !reason.trim())} data-testid="pp-confirm-btn">
              {busy ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" /> Confirm {parseInt(qty) || 0} pc</>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- DONE PHASE ----
  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-[#0b0614] text-white">
        <Header />
        <main className="max-w-md mx-auto px-5 py-16 text-center">
          <div className="inline-flex p-5 rounded-2xl bg-emerald-500/15 border border-emerald-600/40 mb-4">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold" data-testid="pp-done-title">Slip fully packed</h2>
          <p className="text-violet-300/80 text-sm mt-1">{slip?.pick_slip_number} — {boxes.length} carton(s). Hand them to the DC desk.</p>
          {lastBox && (
            <div className="mt-6 rounded-xl bg-[#160d26] border border-violet-800 p-4">
              <div className="text-xs text-violet-400 uppercase">Last carton</div>
              <div className="text-2xl font-black font-mono" data-testid="pp-done-last-box">{lastBox.box_number}</div>
              {lastBox.external_box_no && <div className="text-amber-300 font-mono text-sm">Sticker: {lastBox.external_box_no}</div>}
            </div>
          )}
          <Button className="mt-8 w-full h-14 bg-gradient-to-r from-fuchsia-600 to-violet-700 hover:opacity-90" onClick={() => boot(staffId)} data-testid="pp-next-slip-btn">
            <Truck className="w-5 h-5 mr-2" /> Pick next slip
          </Button>
        </main>
      </div>
    );
  }

  // ---- PACKING PHASE ----
  const totalConfirmed = lines.reduce((s, l) => s + (l.picked_qty || 0), 0);
  const totalPacked = lines.reduce((s, l) => s + (l.packed_qty || 0), 0);
  return (
    <div className="min-h-screen bg-[#0b0614] text-white pb-44">
      <Header right={
        <Badge className="bg-emerald-800 text-emerald-100" data-testid="pp-pack-progress">
          {totalPacked}/{totalConfirmed} packed
        </Badge>
      } />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fuchsia-400/80 font-semibold">
          <ScanLine className="w-4 h-4" /> Step 2 · Scan each piece into a carton
        </div>

        {/* packer group */}
        <div className="rounded-xl bg-[#160d26] border border-violet-900/60 p-3 flex items-center gap-2">
          <span className="text-xs text-violet-300 whitespace-nowrap">Packer group</span>
          <select
            value={group?.group_id || ''}
            onChange={(e) => setGroup(groups.find((g) => g.group_id === e.target.value) || null)}
            className="flex-1 bg-[#0b0614] border border-violet-800 rounded-md px-2 py-2 text-sm text-white"
            data-testid="pp-group-select"
          >
            <option value="">-- Select group --</option>
            {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.group_name} ({(g.members || []).length})</option>)}
          </select>
        </div>

        {/* last closed carton — box number shown big for hand-marking */}
        {lastBox && (
          <div className="rounded-xl bg-emerald-600/15 border border-emerald-700/50 p-4 text-center" data-testid="pp-last-box-banner">
            <div className="text-xs text-emerald-300 uppercase">Write on the carton</div>
            <div className="text-2xl font-black font-mono text-emerald-100" data-testid="pp-last-box-number">{lastBox.box_number}</div>
            {lastBox.external_box_no && <div className="text-amber-300 font-mono text-sm">Sticker: {lastBox.external_box_no}</div>}
          </div>
        )}

        {/* scan input — instant, local (one batch write happens at Close Carton) */}
        <form onSubmit={handleScan} className="flex gap-2">
          <Input ref={scanRef} value={scanInput} onChange={(e) => setScanInput(e.target.value)} autoFocus
            placeholder="Scan USN…" className="text-lg font-mono bg-[#170e28] border-violet-700 text-white h-14" data-testid="pp-scan-input" />
          <Button type="submit" disabled={!scanInput.trim()} className="h-14 px-5 bg-emerald-600 hover:bg-emerald-700" data-testid="pp-scan-submit">
            Add
          </Button>
        </form>

        {/* active carton bar + reviewable scans (undo before closing) */}
        <div className="rounded-xl bg-gradient-to-br from-fuchsia-600/15 to-violet-800/10 border border-violet-700/50 p-4" data-testid="pp-active-carton">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-violet-200">
              <Box className="w-5 h-5 text-fuchsia-300" />
              <span className="text-sm">Active carton</span>
            </div>
            <span className="text-2xl font-black" data-testid="pp-carton-count">{cartonScans.length}</span>
          </div>
          {cartonScans.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="pp-carton-chips">
              {cartonScans.map((s) => (
                <button key={s.usn} onClick={() => removeScan(s.usn)} type="button"
                  className="group inline-flex items-center gap-1 text-xs font-mono bg-[#0b0614] border border-violet-800 rounded-full px-2 py-1 text-violet-200 hover:border-red-500 hover:text-red-300"
                  title="Remove from carton" data-testid={`pp-chip-${s.usn}`}>
                  {s.pack && <span className="text-amber-300" data-testid={`pp-chip-pack-${s.usn}`}>{s.pack} ·</span>}{s.usn}<span className="opacity-60 group-hover:opacity-100">×</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* per-line progress */}
        <div className="rounded-xl bg-[#160d26] border border-violet-900/60 overflow-hidden">
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-violet-400 border-b border-violet-900/60">Items to pack</div>
          {lines.length === 0 ? (
            <div className="p-5 text-center text-emerald-300 text-sm">All confirmed pieces packed 🎉</div>
          ) : lines.map((l) => (
            <div key={l.line_index} className="px-4 py-3 border-b border-violet-950/60 flex items-center justify-between" data-testid={`pp-line-${l.line_index}`}>
              <div>
                <div className="font-medium">{l.design_name || l.barcode}</div>
                <div className="text-xs text-violet-400">{[l.shade, l.size].filter(Boolean).join(' · ')}</div>
              </div>
              <div className="text-right text-sm">
                <div className="text-emerald-300 font-semibold">{l.packed_qty || 0}/{l.picked_qty}</div>
                <div className="text-violet-400 text-xs">{l.remaining_qty} left</div>
              </div>
            </div>
          ))}
        </div>

        {/* extras */}
        {extras.length > 0 && (
          <div className="rounded-xl bg-orange-500/15 border border-orange-600/50 p-4 space-y-3" data-testid="pp-extras-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-orange-200">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                <span className="text-sm font-semibold">{extras.length} extra/over piece(s) kept aside</span>
              </div>
              <Button size="sm" variant="outline" className="border-orange-500 text-orange-200 hover:bg-orange-500/10" onClick={confirmPutaway} data-testid="pp-confirm-putaway-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Put away
              </Button>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {extras.map((p, i) => (
                <div key={p.usn_code + i} className="flex items-center justify-between rounded-lg bg-orange-500/10 px-3 py-1.5 text-sm" data-testid="pp-extra-usn-row">
                  <span className="font-mono font-semibold text-orange-100">{p.usn_code}</span>
                  <span className="text-xs text-orange-300/80">{p.sku_code || ''}{p.original_bin ? ` · ${p.original_bin}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* created cartons */}
        {boxes.length > 0 && (
          <div className="rounded-xl bg-[#160d26] border border-violet-900/60 overflow-hidden">
            <div className="px-4 py-2 text-xs uppercase tracking-wider text-violet-400 border-b border-violet-900/60">Closed cartons ({boxes.length})</div>
            {boxes.map((b, bi) => (
              <div key={b.box_id || b.box_number || bi} className="px-4 py-2 text-sm flex items-center justify-between border-b border-violet-950/60">
                <span className="font-mono">{b.box_number}{b.external_box_no ? ` · ${b.external_box_no}` : ''}</span>
                <span className="text-violet-400">{b.total_qty} pcs</span>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* sticky close-carton bar */}
      <div className="fixed bottom-0 inset-x-0 bg-[#140b22]/95 backdrop-blur border-t border-violet-900/60 p-4">
        <div className="max-w-2xl mx-auto">
          <Button className="w-full h-14 text-base bg-gradient-to-r from-fuchsia-600 to-violet-700 hover:opacity-90 disabled:opacity-40"
            onClick={closeCarton} disabled={busy || cartonScans.length === 0 || !group} data-testid="pp-close-carton-btn">
            <Box className="w-5 h-5 mr-2" /> Close carton ({cartonScans.length} pcs)
          </Button>
        </div>
      </div>

      {/* rejected USNs — show WHICH pieces errored so the team can remove them */}
      <Dialog open={showErrors} onOpenChange={(o) => { setShowErrors(o); if (!o) setPendingClose(null); }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden" data-testid="pp-error-usns-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-500">
              <AlertTriangle className="w-5 h-5" /> {errorUsns.length} piece(s) have errors
            </DialogTitle>
            <DialogDescription>
              {pendingClose
                ? 'The carton is NOT packed yet. Pull these pieces out of the carton, then close the box with the valid ones.'
                : 'These scanned USNs were rejected — pull them out of the carton. Extras are kept aside for put-away.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            {errorUsns.length > 3 && (
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={errorFilter} onChange={(e) => setErrorFilter(e.target.value)}
                  placeholder="Search a USN…"
                  className="pl-9 font-mono bg-[#0b0614] border-violet-800 text-white" data-testid="pp-error-search" />
              </div>
            )}
            {['duplicate', 'not_in_stock', 'extra_or_over'].map((reason) => {
              const q = errorFilter.trim().toUpperCase();
              const rows = errorUsns.filter((e) => e.reason === reason && (!q || (e.usn_code || '').toUpperCase().includes(q)));
              if (rows.length === 0) return null;
              const meta = REASON_META[reason];
              return (
                <div key={reason} data-testid={`pp-error-group-${reason}`}>
                  <div className={`text-xs font-semibold uppercase tracking-wider ${meta.text}`}>{meta.label} · {rows.length}</div>
                  <div className="text-[11px] text-gray-500 mb-2">{meta.hint}</div>
                  <div className="space-y-1">
                    {rows.map((e, i) => {
                      const pos = scanPosOf(e.usn_code);
                      return (
                        <div key={e.usn_code + i} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${meta.bg}`} data-testid="pp-error-usn-row">
                          <div className="flex items-center gap-2 min-w-0">
                            {pos > 0 && (
                              <span className="font-mono text-[11px] font-bold px-1.5 py-0.5 rounded bg-black/40 text-gray-200 shrink-0" data-testid={`pp-error-pos-${e.usn_code}`}>
                                #{pos}<span className="text-gray-500">/{cartonScans.length}</span>
                              </span>
                            )}
                            <span className="font-mono font-semibold shrink-0">{e.usn_code}</span>
                          </div>
                          <span className="text-xs text-right text-gray-300 truncate">{e.design_name || e.sku_code || ''}{e.original_bin ? ` · ${e.original_bin}` : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="shrink-0 flex gap-2 pt-2">
            {pendingClose ? (
              <>
                <Button variant="outline" className="flex-1 border-violet-700 text-violet-200 hover:bg-violet-800/30" onClick={() => { setShowErrors(false); setPendingClose(null); }} disabled={busy} data-testid="pp-error-goback-btn">
                  Go Back &amp; Fix
                </Button>
                {pendingClose.acceptedCount > 0 && (
                  <Button className="flex-1 bg-gradient-to-r from-emerald-600 to-green-700" onClick={proceedCloseAfterAck} disabled={busy} data-testid="pp-error-proceed-btn">
                    Close Box ({pendingClose.acceptedCount} valid)
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" className="flex-1 border-violet-700 text-violet-200 hover:bg-violet-800/30" onClick={copyErrorUsns} data-testid="pp-copy-errors-btn">
                  <Copy className="w-4 h-4 mr-1" /> Copy USNs
                </Button>
                <Button className="flex-1 bg-gradient-to-r from-fuchsia-600 to-violet-700" onClick={() => setShowErrors(false)} data-testid="pp-error-usns-done-btn">
                  Done
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* seasonal small-box sticker */}
      <Dialog open={showSticker} onOpenChange={(o) => { if (!busy) setShowSticker(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="pp-sticker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Box className="w-5 h-5 text-amber-600" /> Small box sticker</DialogTitle>
            <DialogDescription>Scan the pre-printed small-box sticker for this carton.</DialogDescription>
          </DialogHeader>
          <Input autoFocus value={sticker} onChange={(e) => setSticker(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && sticker.trim() && !busy) doCloseCarton(sticker.trim()); }}
            placeholder="Scan sticker…" className="font-mono" data-testid="pp-sticker-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSticker(false)} disabled={busy}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => doCloseCarton(sticker.trim())} disabled={busy || !sticker.trim()} data-testid="pp-sticker-confirm">
              {busy ? 'Closing…' : 'Close carton'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobilePickAndPack;
